<template>
  <div class="route-item">
    <div class="route-summary">
      <div class="route-info">
        <span class="route-idx">Route {{ route?.routeIndex }}</span>
        <span class="route-dist">{{ formateDist(route?.distance) }}</span>
      </div>
      <div class="route-stats">
        <span class="stat-badge">{{ route?.aggregateCount }} samples</span>
      </div>
      <button @click="toggle('averoute')" :class="{ toggled: toggles.averoute }" class="toggle-btn">
        {{ toggles.averoute ? 'Hide Avg' : 'Show Avg' }}
      </button>
    </div>

    <div v-if="route?.refs && route.refs.length > 0" class="source-routes">
      <div class="source-header">Individual Tracks</div>
      <StopPanelRouteSource 
        v-for="(ref) in route?.refs" 
        :key="ref"
        :stopData="stopData" 
        :tostop="tostop" 
        :route="route" 
        :guid="ref" 
        :routeIndex="route.routeIndex" 
        :fromRef="fromRef" 
        :toRef="toRef" 
      />
    </div>
  </div>
</template>

<script setup>
import { defineProps, inject, onBeforeUnmount, reactive, provide } from 'vue';
import StopPanelRouteSource from './StopPanelRouteSource.vue';

const stateData = inject('stateData');
provide('stateData', stateData);

const props = defineProps({
  stopData: {
    type: Object,
    required: true
  },
  tostop: {
    type: Object,
    required: true
  },
  route: {
    type: Object,
    required: true
  },
  fromRef: {
    type: String,
    required: true
  },
  toRef: {
    type: String,
    required: true
  }
});

const toggles = reactive({});
let layers = {};
let layerBuilders = {};

onBeforeUnmount(() => {
  hideAll()
});

function hideAll() {
  for (let name in layers) {
    hideLayer(name);
  }
}
function hideLayer(name) {
  if (layers[name]) {
    stateData.removeLayer(layers[name]);
  }
}

function showLayer(name) {
  if (!layers[name]) {
    if (layerBuilders[name]) {
      layers[name] = layerBuilders[name]();
    }
  }

  if (layers[name]) {
    stateData.addLayer(layers[name]);
  }
}

function toggle(what) {
  toggles[what] = !toggles[what];
  if (!toggles[what]) {
    hideLayer(what);
  } else {
    showLayer(what);
  }
}

function layerbuilder(name, fn) {
  layerBuilders[name] = fn;
}

function formateDist(dist) {
  return Math.round(dist).toString() + "m"
}

function popupbuilder(pos) {
  let str =
    `
    <b>idx</b>: ${pos.idx}</br> 
    <b>Dist</b>: ${pos.dist}</br>
    <b>Lat</b>: ${pos.lat}</br>
    <b>Lon</b>: ${(pos.lon)}</br>`
  return str
}

layerbuilder('averoute', () => {
  if (props.route?.points) {
    let layer = L.layerGroup();
    let eventCol = '#4CAF50'; // Vibrant green
    let gpsCoords = [];

    for (const [index, pos] of props.route.points.entries()) {
      if (pos?.lat && pos?.lon) {
        pos.idx = index
        gpsCoords.push([pos.lat, pos.lon]);

        const marker = L.circleMarker([pos.lat, pos.lon], {
          radius: 4,
          color: 'black',
          fillColor: eventCol,
          fillOpacity: 1
        }).bindTooltip(popupbuilder(pos), { sticky: false });

        marker.addTo(layer);
      }
    }

    const polyline = L.polyline(gpsCoords, { color: eventCol, weight: 4 }).addTo(layer);

    L.polylineDecorator(polyline, {
      patterns: [
        {
          repeat: '50px',
          symbol: L.Symbol.arrowHead({
            pixelSize: 10,
            polygon: false,
            pathOptions: { stroke: true, color: '#FFC107' }
          })
        }
      ]
    }).addTo(layer);

    return layer;
  }
});

</script>

<style scoped>
.route-item {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  overflow: hidden;
}

.route-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  gap: 8px;
}

.route-info {
  display: flex;
  flex-direction: column;
}

.route-idx {
  font-size: 0.75rem;
  font-weight: 600;
  color: #eee;
}

.route-dist {
  font-size: 0.7rem;
  color: #888;
}

.stat-badge {
  background: rgba(255, 255, 255, 0.05);
  color: #aaa;
  font-size: 0.65rem;
  padding: 1px 6px;
  border-radius: 10px;
}

.toggle-btn {
  background: #367530;
  border: none;
  color: white;
  font-size: 0.7rem;
  padding: 3px 8px;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}

.toggle-btn.toggled {
  background: #4CAF50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.4);
}

.source-routes {
  padding: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.source-header {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #555;
  margin-bottom: 4px;
}
</style>